import { ExclamationCircleOutlined, HighlightFilled } from '@ant-design/icons'
import {
  Divider,
  Button,
  Popconfirm,
  Typography,
  message,
  Tooltip,
  Row,
  Col,
  Tabs,
} from 'antd'
import { useCallback } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import dayjs from 'dayjs'
import useQueryParams from '../../hooks/useQueryParams'
import api from '../../api'
import { useAsync } from '../../hooks'
import { toPath } from '../../routes'
import Comments from '../Comments'
import {
  useCurrentUser,
  useFeatureGuard,
  useCanSign,
} from '../../redux/slices/session'
import { ListError, PageHeader } from '../Common'
import ActionButtons from '../Common/ActionButtons'
import PatientSummary from '../Common/PatientSummary'
import PDFPreview from '../Common/PDFPreview'
import Loading from '../Loading'
import DownloadButtons from '../Common/DownloadButtons'
import NavigationButtons from '../Common/NavigationButtons'
import TransmissionSidebar from '../Common/TransmissionSidebar'

const { TabPane } = Tabs
const { Text } = Typography

export default function ShowOfficeCheck() {
  const { practiceId, id } = useParams()
  const navigate = useNavigate()
  const [query] = useQueryParams({})
  const getOfficeCheck = useCallback(
    () => api.officeChecks.get(practiceId, id, query),
    [practiceId, id, query],
  )
  const {
    error,
    pending,
    value: officeCheck,
    execute: reloadReport,
  } = useAsync(getOfficeCheck)
  const { role } = useCurrentUser()
  const isClinician = role === 'clinician'
  const canSign = useCanSign()

  useFeatureGuard('in_office_checks')

  const goToOfficeChecks = useCallback(() => {
    navigate({
      pathname: toPath.practices.officeChecks({
        id,
        practiceId,
      }),
      search: new URLSearchParams(query).toString(),
    })
  }, [navigate, practiceId, id, query])

  const toOfficeCheck = useCallback(
    officeCheckId =>
      officeCheckId && {
        pathname: toPath.practices.officeChecks.officeCheck({
          practiceId,
          id: officeCheckId,
        }),
        search: new URLSearchParams(query).toString(),
      },
    [practiceId, query],
  )

  const toEditReport = () => {
    const path = toPath.practices.officeChecks.officeCheck.edit({
      practiceId,
      id,
    })

    const queryString = new URLSearchParams(query).toString()

    return `${path}?${queryString}`
  }

  const handleDelete = useCallback(async () => {
    try {
      await api.officeChecks.destroy(id, practiceId)
      message.success('Office Check successfully deleted')
      navigate(-1)
    } catch (e) {
      message.error(`There was an error removing this report: ${e.message}`)
    }
  }, [navigate, practiceId, id])

  const { pending: signing, execute: handleSign } = useAsync(async () => {
    try {
      await api.officeChecks.signReport(id, practiceId)
      await reloadReport()
      message.success('The report has been signed')
    } catch (e) {
      if (e.response.errors.bbReport === 'not_present') {
        message.error(
          `There was an error signing this report: Please fill out the BioBridge report before signing.`,
        )
      } else {
        message.error(`There was an error signing this report: ${e.message}`)
      }
    }
  }, false)

  if (pending) {
    return <Loading large />
  }

  if (error) {
    return (
      <ListError
        message="There was an error loading this report"
        description={error.message}
      />
    )
  }

  const { patient } = officeCheck

  const canEdit = () => {
    if (!isClinician) {
      return true
    }

    const now = dayjs.utc()
    const receivedAt = dayjs.utc(officeCheck.receivedAt)

    const diffSeconds = now.diff(receivedAt) / 1000

    // Only allowed for one hour after the report edition
    return diffSeconds <= 3600
  }

  return (
    <div>
      <NavigationButtons
        nextPath={toOfficeCheck(officeCheck.nextOfficeCheckId)}
        prevPath={toOfficeCheck(officeCheck.previousOfficeCheckId)}
      />
      <PageHeader
        title={`${patient.firstName} ${patient.lastName}`}
        onBack={!isClinician && goToOfficeChecks}
        extra={
          <>
            {officeCheck.critical && (
              <>
                <ExclamationCircleOutlined style={{ color: 'red' }} />
                <Text type="danger">Requires Attention</Text>
              </>
            )}
            <Link
              key="edit-patient"
              to={toPath.patients.patient.edit({ id: patient.id })}
            >
              <Button>Edit Patient</Button>
            </Link>
            {canSign &&
              (!officeCheck.biobridgeReport ? (
                <Tooltip
                  placement="left"
                  title="Add a report to be able to sign it"
                >
                  <Button
                    type="dashed"
                    icon={<HighlightFilled />}
                    loading={signing}
                    disabled
                  >
                    Sign
                  </Button>
                </Tooltip>
              ) : (
                <Popconfirm
                  title="Are you sure you want to sign this report?"
                  onConfirm={handleSign}
                  okText="Yes"
                  cancelText="No"
                  placement="topRight"
                >
                  <Button type="dashed" icon={<HighlightFilled />} loading={signing}>
                    Sign
                  </Button>
                </Popconfirm>
              ))}
            {canEdit() && (
              <>
                {officeCheck.isArchived ? (
                  <Button type="dashed" disabled>
                    Archived
                  </Button>
                ) : (
                  <Link key="edit" to={toEditReport()}>
                    <Button>Edit Report</Button>
                  </Link>
                )}
                <DownloadButtons resource={officeCheck} />
              </>
            )}
          </>
        }
      />
      <PatientSummary patient={{ ...patient, practiceId }} />
      <Divider />
      <Row>
        <Col span={16}>
          <Tabs defaultActiveKey="1" className="tabs">
            {officeCheck?.biobridgeReport && (
              <TabPane tab="Report" key="report">
                <PDFPreview path={officeCheck.biobridgeReport} showAllPages />
              </TabPane>
            )}
            {officeCheck.deviceReport && (
              <TabPane tab="Transmission" key="transmission">
                <PDFPreview path={officeCheck.deviceReport} showAllPages />
              </TabPane>
            )}
          </Tabs>
        </Col>
        <Col span={8}>
          <TransmissionSidebar patient={patient} practiceId={practiceId} />
        </Col>
      </Row>

      <ActionButtons onDelete={handleDelete} onlyAdmin={false} />
      {!isClinician && (
        <Comments
          collectionName="office-checks"
          id={officeCheck.id}
          extraPrefix={`practices/${practiceId}`}
        />
      )}
      <style>{`
        .tabs {
          margin-right: 20px;
          max-height: 80vh;
          overflow-y: auto;
        }
      `}</style>
    </div>
  )
}
